import type { NextRouter } from "next/router";
import { UTSLocale } from "@smile2impress/attraction-constants";
import { getLocalizedPathname } from "modules/localize-pathnames/getLocalizedPathname";

import { UTSLocaleToI18nLocale } from "constants/enum";
import {
    normalizePath,
    pathnameFromHref,
    stripLocalePrefix,
} from "utils/localePathUtils";

/**
 * Detects whether a header nav link targets the current page. For dynamic routes
 * (e.g. CMS `[[...slug]]`), `router.pathname` is the route pattern, not the URL;
 * we compare the localized path derived from `link` with the locale-stripped `asPath`.
 */
export const isHeaderNavLinkActive = (
    router: Pick<NextRouter, "pathname" | "asPath" | "locale">,
    linkHref: string | undefined,
    utsLocale: UTSLocale
): boolean => {
    if (!linkHref || linkHref.startsWith("http")) {
        return false;
    }

    const i18nLocale = UTSLocaleToI18nLocale[utsLocale];
    const currentPath = stripLocalePrefix(router.asPath, router.locale);
    const linkPath = pathnameFromHref(linkHref);
    const localizedTarget = getLocalizedPathname(linkPath, i18nLocale, true);
    const expectedPath = normalizePath(localizedTarget ?? linkPath);

    if (currentPath === expectedPath) {
        return true;
    }

    return router.pathname === linkPath;
};
